import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../../context/ThemeContext";
import api from "../../lib/api";
import { toast } from "../ui/sonner";

const Field = ({ label, children }) => (
  <label className="block text-xs text-white/60 space-y-1">
    <span>{label}</span>
    {children}
  </label>
);

// Generic small modal used by every scorecard write (budget / entry / edit / void / members / loan).
export function ScModal({ title, onClose, onSubmit, children, submitLabel = "Save", busy, testid }) {
  const { theme } = useTheme() || {};
  return createPortal(
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-xl border border-white/10 p-4 space-y-3 text-white"
        style={{ background: theme === "light" ? "#ffffff" : "#0f1219" }}
        onClick={(e) => e.stopPropagation()}
        data-testid={testid}
      >
        <div className="text-sm font-semibold">{title}</div>
        {children}
        <div className="flex justify-end gap-2 pt-1">
          <button className="hv-btn-ghost text-xs px-3 py-1.5" onClick={onClose} data-testid={`${testid}-cancel`}>Cancel</button>
          <button className="hv-btn-primary text-xs px-4 py-1.5" disabled={busy} onClick={onSubmit} data-testid={`${testid}-submit`}>
            {busy ? "Saving…" : submitLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

async function run(fn, ok, onDone, setBusy) {
  setBusy(true);
  try {
    await fn();
    toast.success(ok);
    onDone?.();
  } catch (e) {
    toast.error(e?.response?.data?.detail?.toString?.() || "Could not save.");
  } finally {
    setBusy(false);
  }
}

export function BudgetModal({ member, onClose, onDone }) {
  const [budget, setBudget] = useState(String(member.budget ?? 0));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = () => run(() => api.patch(`/scorecard/members/${member.member_id}`, { budget: Number(budget), reason: reason || null }),
    "Budget updated", () => { onDone(); onClose(); }, setBusy);
  return (
    <ScModal title={`Budget — ${member.first_name}`} onClose={onClose} onSubmit={submit} busy={busy} testid="scorecard-budget-modal">
      <Field label="Budget (R)"><input className="hv-input w-full" inputMode="decimal" value={budget} onChange={(e) => setBudget(e.target.value)} data-testid="scorecard-budget-input" /></Field>
      <Field label="Reason (optional)"><input className="hv-input w-full" value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
    </ScModal>
  );
}

export function EntryModal({ member, kind, entry, onClose, onDone }) {
  const editing = !!entry;
  const [k, setK] = useState(entry?.kind || kind || "penalty");
  const [amount, setAmount] = useState(entry ? String(entry.amount) : "");
  const [reason, setReason] = useState(entry?.reason || "");
  const [date, setDate] = useState(entry?.entry_date || new Date().toISOString().slice(0, 10));
  const [why, setWhy] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = () => {
    if (!(Number(amount) > 0) || !reason.trim()) return toast.error("Amount and reason are required.");
    if (editing && !why.trim()) return toast.error("Say why this entry is being changed.");
    return run(() => (editing
      ? api.patch(`/scorecard/entries/${entry.id}`, { kind: k, amount: Number(amount), reason, entry_date: date, reason_for_edit: why })
      : api.post("/scorecard/entries", { member_id: member.member_id, kind: k, amount: Number(amount), reason, entry_date: date })),
    editing ? "Entry updated" : (k === "penalty" ? "Penalty logged" : "Reward logged"), () => { onDone(); onClose(); }, setBusy);
  };
  const voidIt = () => {
    if (!why.trim()) return toast.error("Give a reason for voiding.");
    return run(() => api.post(`/scorecard/entries/${entry.id}/void`, { reason: why }), "Entry voided", () => { onDone(); onClose(); }, setBusy);
  };
  return (
    <ScModal title={editing ? "Edit entry" : `${k === "penalty" ? "Penalty" : "Reward"} — ${member?.first_name || ""}`}
      onClose={onClose} onSubmit={submit} busy={busy} testid="scorecard-entry-modal">
      <div className="flex gap-2">
        {["penalty", "reward"].map((x) => (
          <button key={x} onClick={() => setK(x)} data-testid={`scorecard-entry-kind-${x}`}
            className={`flex-1 text-xs py-1.5 rounded border ${k === x ? (x === "penalty" ? "border-rose-400 bg-rose-500/15 text-rose-200" : "border-emerald-400 bg-emerald-500/15 text-emerald-200") : "border-white/10 text-white/60"}`}>
            {x === "penalty" ? "Penalty" : "Reward"}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Amount (R)"><input className="hv-input w-full" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="scorecard-entry-amount" /></Field>
        <Field label="Date"><input type="date" className="hv-input w-full" value={date} onChange={(e) => setDate(e.target.value)} data-testid="scorecard-entry-date" /></Field>
      </div>
      <Field label="Reason"><input className="hv-input w-full" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="scorecard-entry-reason" /></Field>
      {editing && (
        <>
          <Field label="Why are you changing this? (required)"><input className="hv-input w-full" value={why} onChange={(e) => setWhy(e.target.value)} data-testid="scorecard-entry-why" /></Field>
          {!entry.voided_at && <button onClick={voidIt} className="text-xs text-rose-300 hover:underline" data-testid={`scorecard-entry-void-${entry.id}`}>Void this entry</button>}
          {(entry.edit_log || []).length > 0 && (
            <div className="border-t border-white/10 pt-2 space-y-1 max-h-40 overflow-auto" data-testid={`scorecard-entry-history-${entry.id}`}>
              <div className="text-[10px] uppercase tracking-wider text-white/40">History</div>
              {[...entry.edit_log].reverse().map((l, i) => (
                <div key={i} className="text-[11px] text-white/70">
                  <span className="text-white/40">{(l.at || "").slice(0, 16).replace("T", " ")} · {l.by_name}:</span>{" "}
                  {Object.entries(l.changes || {}).map(([f, c]) => `${f} ${c.before ?? "—"} → ${c.after ?? "—"}`).join(", ")}
                  {l.reason_for_edit ? ` — "${l.reason_for_edit}"` : ""}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </ScModal>
  );
}

export function GroupEntryModal({ project, kind, onClose, onDone }) {
  const members = project.members;
  const [k, setK] = useState(kind);
  const [picked, setPicked] = useState(members.map((m) => m.member_id));
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const submit = () => {
    if (!picked.length) return toast.error("Tick at least one member.");
    if (!(Number(amount) > 0) || !reason.trim()) return toast.error("Amount and reason are required.");
    return run(() => api.post("/scorecard/entries/group", { member_ids: picked, kind: k, amount: Number(amount), reason, entry_date: date }),
      `Group ${k} logged for ${picked.length}`, () => { onDone(); onClose(); }, setBusy);
  };
  return (
    <ScModal title={`Group ${k === "penalty" ? "penalty" : "reward"} — ${project.project_name}`} onClose={onClose} onSubmit={submit}
      busy={busy} submitLabel={`Apply to ${picked.length}`} testid="scorecard-group-modal">
      <div className="flex gap-2">
        {["penalty", "reward"].map((x) => (
          <button key={x} onClick={() => setK(x)} data-testid={`scorecard-group-kind-${x}`}
            className={`flex-1 text-xs py-1.5 rounded border ${k === x ? (x === "penalty" ? "border-rose-400 bg-rose-500/15 text-rose-200" : "border-emerald-400 bg-emerald-500/15 text-emerald-200") : "border-white/10 text-white/60"}`}>
            {x === "penalty" ? "Penalty" : "Reward"}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Amount each (R)"><input className="hv-input w-full" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="scorecard-group-amount" /></Field>
        <Field label="Date"><input type="date" className="hv-input w-full" value={date} onChange={(e) => setDate(e.target.value)} data-testid="scorecard-group-date" /></Field>
      </div>
      <Field label="Reason"><input className="hv-input w-full" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="scorecard-group-reason" /></Field>
      <div className="flex items-center justify-between text-xs text-white/60">
        <span>Members ({picked.length}/{members.length})</span>
        <button className="hover:underline" onClick={() => setPicked(picked.length === members.length ? [] : members.map((m) => m.member_id))} data-testid="scorecard-group-toggle-all">
          {picked.length === members.length ? "Untick all" : "Tick all"}
        </button>
      </div>
      <div className="max-h-56 overflow-auto space-y-1">
        {members.map((m) => (
          <label key={m.member_id} className="flex items-center gap-2 text-sm py-1 cursor-pointer">
            <input type="checkbox" checked={picked.includes(m.member_id)} onChange={() => toggle(m.member_id)} data-testid={`scorecard-group-pick-${m.member_id}`} />
            <span>{m.name || m.first_name}</span>
          </label>
        ))}
      </div>
    </ScModal>
  );
}

export function AddMembersModal({ projectId, defaultBudget, members = [], onClose, onDone }) {
  const existingUserIds = members.map((m) => m.user_id);
  const [current, setCurrent] = useState(members);
  const [staff, setStaff] = useState(null);
  const [picked, setPicked] = useState([]);
  const [budget, setBudget] = useState(String(defaultBudget ?? 0));
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  useEffect(() => {
    api.get("/users").then(({ data }) => {
      const rows = (Array.isArray(data) ? data : data.items || []).filter(
        (u) => u.is_active !== false && u.role !== "client" && !existingUserIds.includes(u.id));
      setStaff(rows);
    }).catch(() => setStaff([]));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const removeMember = async (m) => {
    if (!window.confirm(`Remove ${m.first_name} from this project's scorecard? Their history is kept.`)) return;
    try {
      await api.post(`/scorecard/members/${m.member_id}/remove`, {});
      setCurrent((c) => c.filter((x) => x.member_id !== m.member_id));
      setStaff((s) => (s ? [...s, { id: m.user_id, name: m.name, email: m.email, role: "" }] : s));
      toast.success(`${m.first_name} removed`);
      onDone();
    } catch { toast.error("Could not remove."); }
  };
  const submit = () => (picked.length ? run(() => api.post("/scorecard/members", { project_id: projectId, user_ids: picked, budget: Number(budget) }),
    `${picked.length} added`, () => { onDone(); onClose(); }, setBusy) : toast.error("Pick at least one person."));
  return (
    <ScModal title="Team members" onClose={onClose} onSubmit={submit} busy={busy} submitLabel="Add" testid="scorecard-add-members-modal">
      <div className="space-y-1" data-testid="scorecard-current-members">
        <div className="text-[10px] uppercase tracking-wider text-white/40">On this project ({current.length})</div>
        {current.length === 0 && <div className="text-xs text-white/40">Nobody yet.</div>}
        {current.map((m) => (
          <div key={m.member_id} className="flex items-center gap-2 text-sm py-0.5" data-testid={`scorecard-current-member-${m.member_id}`}>
            <span className="flex-1">{m.name || m.first_name}</span>
            <span className="text-[11px] text-white/40 font-mono">{`R ${Number(m.budget || 0).toLocaleString("en-ZA")}`}</span>
            <button className="text-[11px] text-rose-300 hover:underline" onClick={() => removeMember(m)} data-testid={`scorecard-current-remove-${m.member_id}`}>Remove</button>
          </div>
        ))}
      </div>
      <div className="border-t border-white/10 pt-2 text-[10px] uppercase tracking-wider text-white/40">Add staff</div>
      <Field label="Budget each (R)"><input className="hv-input w-full" inputMode="decimal" value={budget} onChange={(e) => setBudget(e.target.value)} data-testid="scorecard-add-members-budget" /></Field>
      <input className="hv-input w-full" placeholder="Search staff…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="scorecard-add-members-search" />
      <div className="max-h-64 overflow-auto space-y-1">
        {(staff || []).filter((u) => !q || `${u.name || ""} ${u.email || ""} ${u.role || ""}`.toLowerCase().includes(q.toLowerCase())).map((u) => (
          <label key={u.id} className="flex items-center gap-2 text-sm py-1 cursor-pointer">
            <input type="checkbox" checked={picked.includes(u.id)} onChange={() => toggle(u.id)} data-testid={`scorecard-pick-${u.id}`} />
            <span>{u.name || u.email}</span>
            <span className="text-[10px] text-white/40 uppercase">{u.role}</span>
          </label>
        ))}
      </div>
    </ScModal>
  );
}

export function AddProjectModal({ onClose, onDone }) {
  const [items, setItems] = useState(null);
  const [pid, setPid] = useState("");
  const [budget, setBudget] = useState("3000");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/scorecard/available-projects").then(({ data }) => setItems(data.items || [])).catch(() => setItems([]));
  }, []);
  const submit = () => (pid ? run(() => api.post("/scorecard/projects", { project_id: pid, default_budget: Number(budget) }),
    "Project added", () => { onDone(); onClose(); }, setBusy) : toast.error("Pick a project."));
  return (
    <ScModal title="Add project to scorecard" onClose={onClose} onSubmit={submit} busy={busy} submitLabel="Add" testid="scorecard-add-project-modal">
      <Field label="Active project">
        <select className="hv-input w-full" value={pid} onChange={(e) => setPid(e.target.value)} data-testid="scorecard-add-project-select">
          <option value="">Select…</option>
          {(items || []).map((p) => <option key={p.id} value={p.id}>{p.short_id ? `${p.short_id} · ` : ""}{p.name}</option>)}
        </select>
      </Field>
      <Field label="Default budget per member (R)"><input className="hv-input w-full" inputMode="decimal" value={budget} onChange={(e) => setBudget(e.target.value)} /></Field>
    </ScModal>
  );
}

export function BudgetAllModal({ project, onClose, onDone }) {
  const [budget, setBudget] = useState(String(project.default_budget ?? 3000));
  const [busy, setBusy] = useState(false);
  const submit = () => run(() => api.post(`/scorecard/projects/${project.project_id}/budget-all`, { budget: Number(budget) }),
    "Budgets updated", () => { onDone(); onClose(); }, setBusy);
  return (
    <ScModal title={`Set budget for everyone — ${project.project_name}`} onClose={onClose} onSubmit={submit} busy={busy} testid="scorecard-budget-all-modal">
      <Field label="Budget each (R)"><input className="hv-input w-full" inputMode="decimal" value={budget} onChange={(e) => setBudget(e.target.value)} /></Field>
    </ScModal>
  );
}

export function LoanModal({ member, projectName, onClose, onDone }) {
  const [months, setMonths] = useState("3");
  const [start, setStart] = useState(new Date(Date.now() + 20 * 864e5).toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const owed = Math.abs(member.balance);
  const submit = () => run(async () => {
    const { data } = await api.post("/team/loans", {
      user_id: member.user_id, principal: owed, interest_rate_pct: 0, term_months: Number(months), start_date: start,
      reason: `Scorecard deficit – ${projectName}`,
    });
    await api.post(`/scorecard/members/${member.member_id}/loan`, { loan_id: data.id || data.loan?.id });
  }, "Staff loan created", () => { onDone(); onClose(); }, setBusy);
  return (
    <ScModal title={`Create loan — ${member.first_name} owes R ${owed.toLocaleString("en-ZA")}`} onClose={onClose} onSubmit={submit}
      busy={busy} submitLabel="Create loan" testid="scorecard-loan-modal">
      <div className="grid grid-cols-2 gap-2">
        <Field label="Repayment months"><input className="hv-input w-full" inputMode="numeric" value={months} onChange={(e) => setMonths(e.target.value)} data-testid="scorecard-loan-months" /></Field>
        <Field label="First deduction"><input type="date" className="hv-input w-full" value={start} onChange={(e) => setStart(e.target.value)} /></Field>
      </div>
    </ScModal>
  );
}
